// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useState } from "react";
import { useI18n } from "../lib/i18n.js";

/** How often an open tab asks the server whether a new version exists. */
const CHECK_EVERY_MS = 60 * 60_000;

/**
 * The app keeps a copy of itself so it opens fast and works offline. When a
 * new version is installed in the background, tabs that are already open keep
 * running the old one until they reload. This says so, instead of leaving
 * people wondering where a new page went.
 */
export function UpdateBanner({ onBeforeReload }: { onBeforeReload: () => Promise<unknown> }) {
  const { t } = useI18n();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    // The first install also takes control of the page; only a replacement counts as an update.
    let hadController = Boolean(sw.controller);
    const onChange = () => {
      if (hadController) setReady(true);
      hadController = true;
    };
    sw.addEventListener("controllerchange", onChange);
    // Single-page navigation never reloads, so check for a new version now and then.
    const timer = setInterval(() => {
      void sw.getRegistration().then((r) => r?.update().catch(() => undefined));
    }, CHECK_EVERY_MS);
    return () => {
      sw.removeEventListener("controllerchange", onChange);
      clearInterval(timer);
    };
  }, []);

  if (!ready) return null;
  return (
    <div className="card update-banner" role="status">
      <p style={{ marginTop: 0 }}>{t("update.ready")}</p>
      <div className="row">
        <button
          type="button"
          className="primary"
          onClick={() => {
            void onBeforeReload()
              .catch(() => undefined)
              .finally(() => location.reload());
          }}
        >
          {t("update.reload")}
        </button>
        <button type="button" onClick={() => setReady(false)}>
          {t("update.later")}
        </button>
      </div>
    </div>
  );
}
